import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ListOrganizationsRequest, Organization } from 'src/models/organization';
import { FormikProps } from 'formik/dist/types';
import { Formik } from 'formik';
import { GenericMessages } from 'src/intl/GenericMessages';
import { JobMessages } from 'src/intl/JobMessages';
import * as yup from 'yup';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { FamilyOrgShareForm, FamilyOrgShareFormData } from './FamilyOrgShareForm';
import { useListOrganizationsV2Query } from '../../redux/api/organizations';
import type { JobLogPermission } from '../../models/jobLog';
import { getOrgName } from '../../utils/helpers/referenceHelpers';

interface FamilyOrgShareModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
  /**
   * The user's organizations.
   */
  orgFilter: ListOrganizationsRequest;
  permissions?: JobLogPermission[];
  /**
   * Triggers when a form is submitted.
   */
  onSubmit: (values: FamilyOrgShareFormData, orgName: string | undefined) => Promise<void>;
}

/** determines if user can share with a given organization */
function canShareWithOrganization(permissions: JobLogPermission[]) {
  return function filterOrganization(organization: Organization) {
    const alreadyShared = !!permissions.find(p => p.principal === organization._id);
    return !alreadyShared;
  };
}

export const FamilyOrgShareModal: React.FC<FamilyOrgShareModalProps> = ({
  onClose,
  isOpen,
  orgFilter,
  permissions,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const validationSchema = yup.object({
    organization: yup.string().required(formatMessage(GenericMessages.fieldRequiredLabel)),
  });

  const { data: organizations } = useListOrganizationsV2Query(orgFilter);
  const shareableOrganizations = useMemo(
    () => organizations?.filter(canShareWithOrganization(permissions ?? [])) ?? [],
    [organizations, permissions],
  );

  const handleSubmit = useCallback(
    async (values: FamilyOrgShareFormData) => {
      const orgName = values.organization ? getOrgName(values.organization, shareableOrganizations ?? []) : undefined;
      await onSubmit(values, orgName);
    },
    [onSubmit, shareableOrganizations],
  );

  const sortedOrganizations = useMemo(() => {
    return [...shareableOrganizations].sort((a, b) => {
      return a?.orgName > b.orgName ? 1 : -1;
    });
  }, [shareableOrganizations]);

  return (
    <Formik
      initialValues={{
        tubes: true,
        annotations: true,
        timestamps: true,
        pressure: true,
        pieCharts: true,
        idleVsPressure: true,
        configurations: true,
        organization: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps: FormikProps<FamilyOrgShareFormData>) => (
        <form onSubmit={formikProps.handleSubmit}>
          <AppModal
            isOpen={isOpen}
            title={formatMessage(JobMessages.shareReport)}
            onClose={() => {
              onClose();
              formikProps.resetForm();
            }}
            primaryButton={{
              label: formatMessage(JobMessages.shareReport),
              onClick: formikProps.handleSubmit,
              disabled: !formikProps.isValid,
            }}
            secondaryButton={{
              label: formatMessage(GenericMessages.cancelButtonLabel),
              onClick: () => {
                onClose();
                formikProps.resetForm();
              },
            }}
          >
            <FamilyOrgShareForm
              organizations={sortedOrganizations}
              formikProps={formikProps}
              organizationLabel={formatMessage(JobMessages.selectAnInternalOrganization)}
            />
          </AppModal>
        </form>
      )}
    </Formik>
  );
};
export default FamilyOrgShareModal;
